import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { AuthShell } from '../components/auth/AuthShell'
import {
  AuthFormCard,
  AuthField,
  AuthSubmitButton,
} from '../components/auth/AuthFormCard'
import { PasswordInput } from '../components/common'
import authStyles from '../components/auth/AuthFormCard.module.css'
import styles from './LoginPage.module.css'

function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, verifyMFA, startMFAEnrollment, verifyMFAEnrollment } = useAuth()

  const [step, setStep] = useState('login')
  const [credentials, setCredentials] = useState({ email: '', password: '' })
  const [mfaCode, setMfaCode] = useState('')
  const [enrollment, setEnrollment] = useState(null)
  const [authFlowId, setAuthFlowId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const mfaInputRef = useRef(null)

  useEffect(() => {
    if (step === 'mfa' || step === 'mfa-setup') {
      mfaInputRef.current?.focus()
    }
  }, [step])

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await login(credentials.email, credentials.password)

      if (result.requiresMFA) {
        setAuthFlowId(result.authFlowId || '')
        setStep('mfa')
      } else if (result.requiresMFASetup) {
        setAuthFlowId(result.authFlowId || '')
        const setup = await startMFAEnrollment(result.authFlowId)
        setEnrollment({ qrCode: setup.qr_code, secret: setup.secret })
        setStep('mfa-setup')
      } else if (result.success) {
        navigate('/dashboard')
      }
    } catch (err) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleMFASetupVerify = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await verifyMFAEnrollment(authFlowId, mfaCode)
      if (result.success) {
        navigate('/dashboard')
      }
    } catch (err) {
      setError(err.message || 'Invalid MFA code')
    } finally {
      setLoading(false)
    }
  }

  const handleMFAVerify = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await verifyMFA(
        mfaCode,
        credentials.email,
        credentials.password,
        authFlowId
      )

      if (result.success) {
        navigate('/dashboard')
      }
    } catch (err) {
      setError(err.message || 'Invalid MFA code')
    } finally {
      setLoading(false)
    }
  }

  if (step === 'mfa-setup') {
    return (
      <AuthShell>
        <AuthFormCard
          title="Set up two-factor authentication"
          subtitle="Two-factor authentication is required for your account. Scan the QR code with an authenticator app, then enter the 6-digit code to finish signing in."
          onSubmit={handleMFASetupVerify}
          errorMessage={error}
        >
          <div className={styles.qrCode}>
            {enrollment?.qrCode ? <img src={enrollment.qrCode} alt="MFA QR code" /> : null}
          </div>
          <code className={styles.secretCode}>{enrollment?.secret}</code>
          <AuthField
            id="mfa_setup_code"
            label="Authentication code"
            type="text"
            value={mfaCode}
            onChange={setMfaCode}
            placeholder="000000"
            maxLength={6}
            pattern="[0-9]{6}"
            inputMode="numeric"
            autoComplete="one-time-code"
            inputRef={mfaInputRef}
            required
            autoFocus
            disabled={loading}
            inputClassName={styles.mfaInput}
          />
          <AuthSubmitButton
            loading={loading}
            disabled={mfaCode.length !== 6}
            loadingLabel="Verifying..."
          >
            Verify and continue
          </AuthSubmitButton>
          <button
            type="button"
            onClick={() => {
              setStep('login')
              setMfaCode('')
              setAuthFlowId('')
              setEnrollment(null)
              setError('')
            }}
            className={styles.backButton}
            disabled={loading}
          >
            <ArrowLeft size={15} />
            Back to Login
          </button>
        </AuthFormCard>
      </AuthShell>
    )
  }

  if (step === 'mfa') {
    return (
      <AuthShell>
        <AuthFormCard
          title="Two-factor authentication"
          subtitle="Enter the 6-digit code from your authenticator app."
          onSubmit={handleMFAVerify}
          errorMessage={error}
        >
          <AuthField
            id="mfa_code"
            label="Authentication code"
            type="text"
            value={mfaCode}
            onChange={setMfaCode}
            placeholder="000000"
            maxLength={6}
            pattern="[0-9]{6}"
            inputMode="numeric"
            autoComplete="one-time-code"
            inputRef={mfaInputRef}
            required
            autoFocus
            disabled={loading}
            inputClassName={styles.mfaInput}
          />

          <AuthSubmitButton
            loading={loading}
            disabled={mfaCode.length !== 6}
            loadingLabel="Verifying..."
          >
            Verify
          </AuthSubmitButton>

          <button
            type="button"
            onClick={() => {
              setStep('login')
              setMfaCode('')
              setAuthFlowId('')
              setError('')
            }}
            className={styles.backButton}
            disabled={loading}
          >
            <ArrowLeft size={15} />
            Back to Login
          </button>
        </AuthFormCard>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <AuthFormCard
        title="Sign in"
        subtitle="Sign in with your company account."
        onSubmit={handleLogin}
        errorMessage={error}
        successMessage={location.state?.passwordReset ? 'Password updated. Sign in with your new password.' : ''}
      >
        <AuthField
          id="email"
          label="Email"
          type="email"
          value={credentials.email}
          onChange={(value) => setCredentials({ ...credentials, email: value })}
          placeholder="you@company.com"
          required
          autoFocus
          autoComplete="username"
          disabled={loading}
        />

        <PasswordInput
          id="password"
          label="Password"
          value={credentials.password}
          onChange={(value) => setCredentials({ ...credentials, password: value })}
          placeholder="Enter your password"
          required
          disabled={loading}
          autoComplete="current-password"
          size="large"
        />

        <AuthSubmitButton loading={loading} loadingLabel="Signing in...">
          Sign In
        </AuthSubmitButton>

        <div className={authStyles.supportRow}>
          <Link to="/forgot-password" className={authStyles.textLink}>
            Forgot password?
          </Link>
        </div>
      </AuthFormCard>
    </AuthShell>
  )
}

export default LoginPage
